import { FC, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Layout, Row, Spin, Space } from "antd";
import { DataTableFilter } from "../../design-system";
import { useNavigate } from "react-router-dom";
import { listProjects } from "../../common/api/ProjectClient";
import { ProjectSummary } from "../../types/ProjectTypes";
import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { ProjectsTable } from "./components/ProjectsTable";
import classes from "./ProjectListView.module.less";
import { ModalGuidedIntake } from "./components/ModalGuidedIntake";

const { Header, Content } = Layout;

const OWNER_FILTER_ALL = "all";
const OWNER_FILTER_MINE = "mine";
const OWNER_FILTER_STORAGE_KEY = "projectListOwnerFilter";

export const ProjectListView: FC = () => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    // Only the first load shows the spinner: a refresh after an edit in the
    // table (owner, category) swaps the rows in place.
    const [loading, setLoading] = useState(true);
    const [guidedIntakeOpen, setGuidedIntakeOpen] = useState(false);

    const fetchProjects = async () => {
        try {
            setProjects(await listProjects());
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    // Closing the guided intake dialog refreshes the list, so a draft created
    // during the session shows on top (the list is sorted by last update).
    const handleGuidedIntakeClose = () => {
        setGuidedIntakeOpen(false);
        void fetchProjects();
    };

    // Most recently updated first.
    const sortedProjects = useMemo(
        () =>
            [...projects].sort(
                (a, b) =>
                    new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime()
            ),
        [projects]
    );

    // Mine: the projects the user owns or collaborates on.
    const ownerFilters: DataTableFilter<ProjectSummary>[] = [
        {
            key: OWNER_FILTER_MINE,
            label: "My projects",
            match: (p) => p.ownerId === me.id || (p.collaboratorIds ?? []).includes(me.id),
            emptyText: "No projects of yours yet. Switch to all projects, or start one above.",
        },
        { key: OWNER_FILTER_ALL, label: "All projects" },
    ];

    return (
        <Layout className={classes.layout}>
            <Header className={classes.header}>
                <Row justify="space-between" align="middle" className={classes.titleRow}>
                    <div>
                        <h2 style={{ margin: 0 }}>Procurement Hub</h2>
                        <div className={classes.subtitle}>
                            Upload your contracts or proposals and submit for analysis to start
                            optimizing your vendor spend.
                        </div>
                    </div>
                    {/* One entry point for any project type: the guided intake
                        determines deal vs RFx during the conversation. */}
                    <Button
                        type="primary"
                        size="large"
                        className={classes.creator}
                        onClick={() => setGuidedIntakeOpen(true)}
                    >
                        + New Project
                    </Button>
                </Row>
            </Header>

            <Content className={classes.content}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row>
                        <Col span={24}>
                            {loading ? (
                                <Card>
                                    <div style={{ textAlign: "center", padding: 48 }}>
                                        <Spin size="large" />
                                    </div>
                                </Card>
                            ) : (
                                <ProjectsTable
                                    projects={sortedProjects}
                                    onUpdate={fetchProjects}
                                    filters={ownerFilters}
                                    defaultFilter={
                                        sessionStorage.getItem(OWNER_FILTER_STORAGE_KEY) ??
                                        OWNER_FILTER_MINE
                                    }
                                    onFilterChange={(key) =>
                                        sessionStorage.setItem(OWNER_FILTER_STORAGE_KEY, key)
                                    }
                                    emptyText="No projects yet. Use the button above to analyze your contracts or proposals."
                                />
                            )}
                        </Col>
                    </Row>
                </Space>
            </Content>

            <ModalGuidedIntake
                open={guidedIntakeOpen}
                onClose={handleGuidedIntakeClose}
                onSubmitted={(submittedProjectId) => {
                    setGuidedIntakeOpen(false);
                    navigate(`/project/${submittedProjectId}/analysis/1`);
                }}
            />
        </Layout>
    );
};
